import React, { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Textarea,
} from '@roger/ui-components';
import { client } from '../../services/api';
import {
  getAvailableCliniciansUserClinicsAvailableGet,
  getCms485PrefillPatientsPatientIdCms485PrefillGet,
  generateCms485PatientsPatientIdCms485GeneratePost,
  listCms485OasisVisitsPatientsPatientIdCms485OasisVisitsGet,
} from '../../services/api/sdk.gen';
import type { AvailableClinicianResponse } from '../../services/api/types.gen';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';

type PhysicianOption = AvailableClinicianResponse;

function physicianDisplayName(p: PhysicianOption): string {
  if (p.clinician_first_name && p.clinician_last_name) {
    return `${p.clinician_first_name} ${p.clinician_last_name}`;
  }
  return `${p.user_first_name} ${p.user_last_name}`;
}

const DEFAULT_HOMEBOUND_STATEMENT =
  'I certify/ recertify that this patient is confined to his/her home and needs intermittent skilled nursing care, physical therapy and/or speech therapy or continues to need occupational therapy. This patient is under my care, and I have authorized the services on this plan of care and I or another physician will periodically review this plan. I attest that a valid face-to-face encounter occurred (or will occur) within timeframe requirements and it is related to the primary reason the patient requires home health services.';

const TEST_DEFAULTS = {
  dmeSupplies: [
    'Wheelchair',
    'Probe Covers',
    'Sterile Gloves',
    'Elevated Toilet Seat',
    'Hospital Bed with Side Rails',
  ].join('\n'),
  riskProfile: [
    'Risk Factors:',
    '- History of falls (2 falls in the past 6 months)',
    '- Multiple hospitalizations (3 in the past year)',
    '- Currently taking 8 medications',
    '- Lives alone with limited caregiver support',
    '- Impaired mobility requiring assistive device',
  ].join('\n'),
  ordersAndTreatments: [
    'SN: 3x/wk x 2 wks, then 2x/wk x 4 wks for wound care, medication management, and disease education.',
    'PT: 3x/wk x 3 wks, then 2x/wk x 3 wks for gait training, therapeutic exercises, and fall prevention.',
    'OT: 2x/wk x 4 wks for ADL training and home safety evaluation.',
    '',
    'Homebound Reason: Patient requires taxing effort to leave the home due to severe shortness of breath on exertion and impaired mobility. Patient requires assistive device and human assistance to ambulate.',
  ].join('\n'),
  safetyMeasures: [
    'Keep pathways clear of clutter and tripping hazards',
    'Fall precautions: non-skid footwear, grab bars in bathroom',
    'Instructed on proper use of DME and electrical safety',
    'Emergency numbers posted by telephone',
    'Medication safety: proper storage and disposal',
  ].join('\n'),
  nutritionalRequirements: [
    'Diet: 1800 Calorie ADA Diet',
    'No Concentrated Sweets',
    'Low Sodium (< 2g/day)',
    'Encourage adequate fluid intake (6-8 glasses/day)',
    'Monitor weight weekly and report changes > 2 lbs',
  ].join('\n'),
  goals: [
    '1. Patient will demonstrate wound healing with 50% reduction in wound size within 60 days.',
    '2. Patient will verbalize understanding of medication regimen and potential side effects within 2 weeks.',
    '3. Patient will ambulate 150 feet with least restrictive device with standby assist within 30 days.',
    '4. Patient will perform ADLs with modified independence within 4 weeks.',
    '5. Patient weight will be maintained between 150 and 160 lbs during the episode.',
  ].join('\n'),
};

interface CMS485GenerateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  episodeId?: string;
  onSuccess: () => void;
  onError: (title: string, message: string) => void;
  onGenerated: (title: string, message: string) => void;
}

const CMS485GenerateDialog: React.FC<CMS485GenerateDialogProps> = ({
  open,
  onOpenChange,
  patientId,
  episodeId,
  onSuccess,
  onError,
  onGenerated,
}) => {
  const { hasPermission } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
    }))
  );
  const [oasisVisits, setOasisVisits] = useState<
    Array<{
      visit_id: string;
      visit_date: string;
      visit_type: string;
      display_name: string;
    }>
  >([]);
  const [selectedVisitId, setSelectedVisitId] = useState<string | undefined>();
  const [dmeSupplies, setDmeSupplies] = useState('');
  const [riskProfile, setRiskProfile] = useState('');
  const [ordersAndTreatments, setOrdersAndTreatments] = useState('');
  const [homeboundStatement, setHomeboundStatement] = useState('');
  const [homeboundReasons, setHomeboundReasons] = useState('');
  const [useTemplate, setUseTemplate] = useState(false);
  const [safetyMeasures, setSafetyMeasures] = useState('');
  const [nutritionalRequirements, setNutritionalRequirements] = useState('');
  const [goals, setGoals] = useState('');
  const [physicians, setPhysicians] = useState<PhysicianOption[]>([]);
  const [selectedPhysicianUcId, setSelectedPhysicianUcId] = useState('');
  const [referringPhysician, setReferringPhysician] = useState('');
  const [physicianNpi, setPhysicianNpi] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPrefilling, setIsPrefilling] = useState(false);

  const applyPrefillData = useCallback(
    (data: Record<string, unknown>, physicianList: PhysicianOption[]) => {
      if (data.dme_supplies) setDmeSupplies(data.dme_supplies as string);
      if (data.safety_measures)
        setSafetyMeasures(data.safety_measures as string);
      if (data.nutritional_requirements)
        setNutritionalRequirements(data.nutritional_requirements as string);
      if (data.risk_profile) setRiskProfile(data.risk_profile as string);
      if (data.homebound_reasons)
        setHomeboundReasons(data.homebound_reasons as string);
      if (data.goals) setGoals(data.goals as string);
      const physicianName = (data.referring_physician as string) || '';
      const physicianNpiValue = (data.physician_npi as string) || '';
      setReferringPhysician(physicianName);
      setPhysicianNpi(physicianNpiValue);
      // Pre-select from dropdown if a matching physician is found by name
      if (physicianName && physicianList.length > 0) {
        const match = physicianList.find(
          p =>
            physicianDisplayName(p).toLowerCase() ===
            physicianName.toLowerCase()
        );
        if (match) setSelectedPhysicianUcId(match.id);
      }
    },
    []
  );

  useEffect(() => {
    if (!open) return;
    // Reset all fields so stale data never persists across patients or episodes
    setOasisVisits([]);
    setSelectedVisitId(undefined);
    setDmeSupplies('');
    setSafetyMeasures('');
    setNutritionalRequirements('');
    setRiskProfile('');
    setOrdersAndTreatments('');
    setHomeboundStatement('');
    setHomeboundReasons('');
    setGoals('');
    setUseTemplate(false);
    setPhysicians([]);
    setSelectedPhysicianUcId('');
    setReferringPhysician('');
    setPhysicianNpi('');
    setOrderNumber('');

    let cancelled = false;
    const run = async () => {
      setIsPrefilling(true);
      try {
        // 1. Fetch eligible OASIS visits and physicians list in parallel
        const [visitsRes, physiciansRes] = await Promise.all([
          listCms485OasisVisitsPatientsPatientIdCms485OasisVisitsGet({
            client,
            path: { patient_id: patientId },
            query: episodeId ? { episode_id: episodeId } : undefined,
          }),
          getAvailableCliniciansUserClinicsAvailableGet({ client }),
        ]);
        if (cancelled) return;

        const visits = visitsRes.data ?? [];
        const allClinicians = physiciansRes.data ?? [];
        if (cancelled) return;

        const physicianList = allClinicians.filter(c => c.is_physician);
        setPhysicians(physicianList);
        setOasisVisits(visits);

        // 2. Fetch prefill (with or without an OASIS visit)
        const prefillQuery: { episode_id?: string; visit_id?: string } = {};
        if (episodeId) prefillQuery.episode_id = episodeId;
        if (visits.length > 0) {
          const firstVisitId = visits[0].visit_id;
          setSelectedVisitId(firstVisitId);
          prefillQuery.visit_id = firstVisitId;
        }
        const prefillRes =
          await getCms485PrefillPatientsPatientIdCms485PrefillGet({
            client,
            path: { patient_id: patientId },
            query: prefillQuery,
          });
        if (!prefillRes.data || cancelled) return;
        if (!cancelled) applyPrefillData(prefillRes.data, physicianList);
      } catch {
        // prefill is best-effort; silently skip on error
      } finally {
        if (!cancelled) setIsPrefilling(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [open, patientId, episodeId, applyPrefillData]);

  const handleVisitChange = async (visitId: string) => {
    setSelectedVisitId(visitId);
    // Reset only OASIS-sourced fields; leave narrative fields (orders, homebound statement) intact
    setDmeSupplies('');
    setSafetyMeasures('');
    setNutritionalRequirements('');
    setRiskProfile('');
    setHomeboundReasons('');
    setGoals('');
    setIsPrefilling(true);
    try {
      const prefillRes =
        await getCms485PrefillPatientsPatientIdCms485PrefillGet({
          client,
          path: { patient_id: patientId },
          query: {
            visit_id: visitId,
            ...(episodeId ? { episode_id: episodeId } : {}),
          },
        });
      if (!prefillRes.data) return;
      applyPrefillData(prefillRes.data, physicians);
    } catch {
      // best-effort
    } finally {
      setIsPrefilling(false);
    }
  };

  const handlePhysicianChange = (ucId: string) => {
    setSelectedPhysicianUcId(ucId);
    const physician = physicians.find(p => p.id === ucId);
    if (physician) {
      setReferringPhysician(physicianDisplayName(physician));
      setPhysicianNpi(physician.clinician_npi || '');
    }
  };

  const handleFillTestData = () => {
    setDmeSupplies(TEST_DEFAULTS.dmeSupplies);
    setRiskProfile(TEST_DEFAULTS.riskProfile);
    setOrdersAndTreatments(TEST_DEFAULTS.ordersAndTreatments);
    setSafetyMeasures(TEST_DEFAULTS.safetyMeasures);
    setNutritionalRequirements(TEST_DEFAULTS.nutritionalRequirements);
    setGoals(TEST_DEFAULTS.goals);
    setHomeboundStatement(DEFAULT_HOMEBOUND_STATEMENT);
    setUseTemplate(true);
  };

  const handleTemplateToggle = (checked: boolean) => {
    setUseTemplate(checked);
    if (checked) {
      setHomeboundStatement(DEFAULT_HOMEBOUND_STATEMENT);
    } else {
      setHomeboundStatement('');
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const response = await generateCms485PatientsPatientIdCms485GeneratePost({
        client,
        path: { patient_id: patientId },
        query: episodeId ? { episode_id: episodeId } : undefined,
        body: {
          dme_supplies: dmeSupplies,
          risk_profile: riskProfile,
          orders_and_treatments: ordersAndTreatments,
          homebound_certification_statement: homeboundStatement,
          safety_measures: safetyMeasures,
          nutritional_requirements: nutritionalRequirements,
          goals,
          referring_physician: referringPhysician,
          physician_npi: physicianNpi,
          order_number: orderNumber,
        },
      });
      if (response.error) {
        throw new Error('Failed to generate CMS-485');
      }
      const blob = response.data as Blob;
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      URL.revokeObjectURL(url);
      onGenerated(
        'CMS-485 Generated',
        'The document has been saved to patient documents.'
      );
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to generate CMS-485:', err);
      onError(
        'Generation Failed',
        'Failed to generate CMS-485. Please try again.'
      );
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <div className='flex items-center justify-between'>
            <DialogTitle>Generate CMS-485</DialogTitle>
            {hasPermission('debug:access') && (
              <Button
                variant='outline'
                size='sm'
                onClick={handleFillTestData}
                disabled={isGenerating}
              >
                Fill Test Data
              </Button>
            )}
          </div>
          {oasisVisits.length > 0 && (
            <div className='flex items-center gap-2 mt-2'>
              <Label className='shrink-0 text-sm'>OASIS Visit</Label>
              <Select
                value={selectedVisitId}
                onValueChange={handleVisitChange}
                disabled={isPrefilling || isGenerating}
              >
                <SelectTrigger selectSize='sm' className='max-w-xs'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {oasisVisits.map(v => (
                    <SelectItem key={v.visit_id} value={v.visit_id}>
                      {v.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <p className='text-sm text-muted-foreground'>
            {isPrefilling
              ? 'Loading prefilled data from OASIS visit…'
              : oasisVisits.length === 0
                ? 'No OASIS visit found for this episode. Fill in fields manually.'
                : 'Fields below are prefilled from the selected OASIS visit. Edit as needed before generating.'}
          </p>
        </DialogHeader>

        <div className='space-y-5 py-2'>
          {physicians.length > 0 && (
            <div className='space-y-1.5'>
              <Label>Certifying Physician</Label>
              <Select
                value={selectedPhysicianUcId}
                onValueChange={handlePhysicianChange}
                disabled={isPrefilling || isGenerating}
              >
                <SelectTrigger selectSize='sm'>
                  <SelectValue
                    placeholder={referringPhysician || 'Select physician…'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {physicians.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {physicianDisplayName(p)}
                      {p.clinician_npi ? ` — NPI ${p.clinician_npi}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {referringPhysician && !selectedPhysicianUcId && (
                <p className='text-xs text-muted-foreground'>
                  From referral: {referringPhysician}
                </p>
              )}
            </div>
          )}

          <div className='flex gap-4'>
            <div className='space-y-1.5 flex-1'>
              <Label>Order Number</Label>
              <Input
                value={orderNumber}
                onChange={e => setOrderNumber(e.target.value)}
                placeholder='Optional'
                disabled={isGenerating}
              />
            </div>
          </div>

          <Separator />

          <div className='space-y-1.5'>
            <Label>DME &amp; Supplies</Label>
            <Textarea
              value={dmeSupplies}
              onChange={e => setDmeSupplies(e.target.value)}
              rows={3}
            />
          </div>

          <div className='space-y-1.5'>
            <Label>Patient Risk Profile</Label>
            <Textarea
              value={riskProfile}
              onChange={e => setRiskProfile(e.target.value)}
              rows={3}
            />
          </div>

          <Separator />

          <div className='space-y-1.5'>
            <Label>Orders &amp; Treatments</Label>
            <Textarea
              value={ordersAndTreatments}
              onChange={e => setOrdersAndTreatments(e.target.value)}
              rows={6}
            />
          </div>

          <Separator />

          <div className='space-y-1.5'>
            <Label>Home Bound Certification Statement</Label>
            {homeboundReasons && (
              <div className='text-sm text-muted-foreground bg-muted rounded p-2 whitespace-pre-line'>
                <span className='font-medium'>
                  Homebound reasons from OASIS:
                </span>
                {'\n'}
                {homeboundReasons}
              </div>
            )}
            <div className='flex items-center gap-2 mb-2'>
              <Checkbox
                id='use-template'
                checked={useTemplate}
                onCheckedChange={checked => handleTemplateToggle(!!checked)}
              />
              <label
                htmlFor='use-template'
                className='text-sm text-muted-foreground cursor-pointer'
              >
                Use standard template
              </label>
            </div>
            <Textarea
              value={homeboundStatement}
              onChange={e => {
                setHomeboundStatement(e.target.value);
                if (
                  e.target.value !== DEFAULT_HOMEBOUND_STATEMENT &&
                  useTemplate
                ) {
                  setUseTemplate(false);
                }
              }}
              rows={4}
              maxLength={750}
            />
            <p className='text-xs text-muted-foreground text-right'>
              {homeboundStatement.length}/750 character limit
            </p>
          </div>

          <Separator />

          <div className='space-y-1.5'>
            <Label>Safety Measures</Label>
            <Textarea
              value={safetyMeasures}
              onChange={e => setSafetyMeasures(e.target.value)}
              rows={3}
            />
          </div>

          <div className='space-y-1.5'>
            <Label>Nutritional Requirements</Label>
            <Textarea
              value={nutritionalRequirements}
              onChange={e => setNutritionalRequirements(e.target.value)}
              rows={3}
            />
          </div>

          <div className='space-y-1.5'>
            <Label>Goals &amp; Measurable Outcomes</Label>
            <Textarea
              value={goals}
              onChange={e => setGoals(e.target.value)}
              rows={5}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isGenerating}
          >
            Cancel
          </Button>
          <Button onClick={handleGenerate} disabled={isGenerating}>
            {isGenerating && <Loader2 className='h-4 w-4 mr-1 animate-spin' />}
            Generate CMS-485
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CMS485GenerateDialog;
